// src/analysis/sessionAnalysis/zoneDisplay/recalibratedLapPoints.ts
import type { Lap } from '../../../domain/lap/Lap'
import {
  calculateTimeDrift,
  selectRecalibrationReference,
  RECALIBRATION_DRIFT_OPTIONS,
} from '../../../domain/lap/TimeDriftCalculator'
import {
  computeBaseDataPoints,
  applyTimeDriftOverlay,
} from '../../../processors/lapComparison/twoPhaseProcessing'
import type {
  PreprocessedDataPoint,
  RecalibrationRefData,
} from '../../../processors/lapComparison/types'
import { batchRecalibrateRaw } from '../../../utils/distanceUtils'

/**
 * One lap's points placed on the comparison's recalibrated distance frame.
 * `points[].distance` is recalibrated (reference lap keeps its raw distance).
 */
export interface RecalibratedLap {
  lapId: string
  lapNumber: number
  sessionId?: string
  isReference: boolean
  points: PreprocessedDataPoint[]
}

/**
 * Pure, framework-free equivalent of mobile's useTimeDriftData + useComparisonCache
 * (minus React caching, coasting, and stats). Each non-reference lap is remapped
 * onto the reference lap's distance channel so every lap shares one distance frame;
 * the reference keeps its raw distance.
 *
 * This is the shared recalibration the web zone charts need; mobile composes the
 * same pipeline inside useComparisonCache. Both sides take the reference rule and
 * drift smoothing from selectRecalibrationReference / RECALIBRATION_DRIFT_OPTIONS
 * so the two frames cannot drift apart.
 */
export function buildRecalibratedLapPoints(laps: Lap[]): RecalibratedLap[] {
  if (laps.length === 0) return []

  const reference = selectRecalibrationReference(laps)

  let refData: RecalibrationRefData | undefined
  if (reference) {
    const gps = reference.getGpsPoints()
    const refLats = new Array<number>(gps.length)
    const refLons = new Array<number>(gps.length)
    const refDistances = new Array<number>(gps.length)
    for (let i = 0; i < gps.length; i++) {
      refLats[i] = gps[i].latitude
      refLons[i] = gps[i].longitude
      refDistances[i] = gps[i].distanceMeters
    }
    refData = { refLats, refLons, refDistances }
  }

  return laps.map((lap) => {
    const base = computeBaseDataPoints(lap, new Map())
    const isReference = reference?.id === lap.id

    let points: PreprocessedDataPoint[]
    if (!reference || isReference) {
      // Fast path: raw distance frame (no drift, no recalibration).
      points = applyTimeDriftOverlay(base, undefined, undefined)
    } else {
      const drift = calculateTimeDrift(
        reference,
        lap,
        RECALIBRATION_DRIFT_OPTIONS
      )
      points = applyTimeDriftOverlay(base, drift, refData, batchRecalibrateRaw)
    }

    return {
      lapId: lap.id,
      lapNumber: lap.lapNumber,
      sessionId: lap.sessionId,
      isReference,
      points,
    }
  })
}
